import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import PathwayBadge from "./PathwayBadge";
import { Link } from "react-router-dom";

// A nav item never demands more width than its container gives it; the label
// truncates instead. Consumers must still size the track (e.g.
// grid-template-columns: minmax(0, 1fr)) or an auto track resolves to
// max-content and the rail widens.
const TONES = {
    default: {
        color: "var(--pw-neutral-800)",
        activeColor: "var(--text-heading)",
        iconColor: "var(--pw-neutral-500)",
        hoverBackground: "var(--pw-neutral-75)",
    },
    danger: {
        color: "var(--pw-danger)",
        activeColor: "var(--pw-danger)",
        iconColor: "var(--pw-danger)",
        hoverBackground: "var(--pw-danger-bg)",
    },
};

// A nav item that navigates has to be an anchor: a button cannot be cmd-clicked into a new
// tab, has no context menu and offers no target to copy. Items that only act — opening a
// dialog or the help centre — stay buttons, so the element follows what the item does.
const Row = styled.button`
    /* As a link it inherits the global anchor styling, which underlined every rail entry the
       moment they stopped being buttons. */
    text-decoration: none;

    &:hover {
        text-decoration: none;
    }

    display: flex;
    align-items: center;
    gap: var(--gap-l);
    width: 100%;
    min-width: 0;
    max-width: 100%;
    padding: 10px 12px;
    border: none;
    border-radius: var(--pw-radius-md);
    background: ${(props) => {
        if (props.$checked || props.$active) {
            return props.$iconBadge ? "var(--pw-neutral-100)" : "var(--pw-neutral-75)";
        }
        return "transparent";
    }};
    color: ${(props) =>
        props.$checked || props.$active ? TONES[props.$tone].activeColor : TONES[props.$tone].color};
    font-family: inherit;
    font-size: var(--pw-text-base);
    font-weight: ${(props) => (props.$active || props.$checked ? "var(--pw-weight-semibold)" : "var(--pw-weight-light)")};
    text-align: left;
    white-space: nowrap;
    cursor: pointer;
    transition: background var(--pw-transition-fast) var(--pw-ease);

    &:hover {
        background: ${(props) =>
            props.$iconBadge ? "var(--pw-neutral-75)" : TONES[props.$tone].hoverBackground};
    }
`;

const Icon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${(props) => (props.$iconBadge ? "28px" : "1.05em")};
    height: ${(props) => (props.$iconBadge ? "28px" : "auto")};
    flex: none;
    line-height: 1;
    border-radius: ${(props) => (props.$iconBadge ? "7px" : "0")};
    background: ${(props) => {
        if (!props.$iconBadge) return "transparent";
        return props.$checked ? "var(--pw-brand-cyan)" : "var(--pw-neutral-100)";
    }};
    color: ${(props) => {
        if (props.$iconBadge) {
            return props.$checked ? "var(--text-on-brand)" : "var(--pw-neutral-600)";
        }
        return props.$active ? "var(--pw-primary)" : TONES[props.$tone].iconColor;
    }};
`;

const Label = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Attention = styled.span`
    flex: none;
    width: 20px;
    padding: 1px 0;
    border-radius: 7px;
    background: var(--pw-warning-bg);
    color: var(--pw-warning-fg);
    font-size: 10px;
    font-weight: var(--pw-weight-bold);
    text-align: center;
`;

const Check = styled.span`
    display: inline-flex;
    align-items: center;
    flex: none;
    color: var(--pw-primary);
`;

export const PathwayNavItem = ({
    label,
    icon,
    active = false,
    count,
    attention = false,
    checked = false,
    iconBadge = false,
    tone = "default",
    onClick,
    to,
    dataCy,
}) => (
    <Row
        as={to ? Link : undefined}
        to={to}
        type={to ? undefined : "button"}
        $active={active}
        $checked={checked}
        $iconBadge={iconBadge}
        $tone={TONES[tone] ? tone : "default"}
        aria-current={active ? "page" : undefined}
        onClick={onClick}
        data-cy={dataCy}
    >
        {icon && (
            <Icon $active={active} $checked={checked} $iconBadge={iconBadge} $tone={TONES[tone] ? tone : "default"}>
                {icon}
            </Icon>
        )}
        <Label>{label}</Label>
        {attention && <Attention aria-label="Braucht Aufmerksamkeit">!</Attention>}
        {!attention && Boolean(count) && <PathwayBadge variant="count">{count}</PathwayBadge>}
        {checked && (
            <Check aria-label="Ausgewählt">
                <i className="fa fa-check" aria-hidden="true" />
            </Check>
        )}
    </Row>
);

PathwayNavItem.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.node,
    active: PropTypes.bool,
    count: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    attention: PropTypes.bool,
    checked: PropTypes.bool,
    iconBadge: PropTypes.bool,
    tone: PropTypes.oneOf(["default", "danger"]),
    onClick: PropTypes.func,
    // A route the item leads to. Given one, the item renders as a link.
    to: PropTypes.string,
    dataCy: PropTypes.string,
};

export default PathwayNavItem;
